import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Loader } from "@/components/Atoms";
import { Trash2, Gamepad2, Check, RotateCw } from "lucide-react";
import { ChilletSkull } from "@/components/ChilletSkull";

export default function Linking() {
  const { refreshProfile } = useAuth();
  const toast = useToast();
  const [linked, setLinked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);

  // ---- Click-to-Link state -----------------------------------------------
  const [players, setPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(true);
  const [pending, setPending] = useState(null);          // {server_name, player_name, steam_id, sent_at}
  const [confirmCode, setConfirmCode] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    api.get("/link/status").then(({ data }) => {
      setLinked(data.linked_servers || []);
    }).finally(() => setLoading(false));
  }, []);

  // Poll online-players every 6s so users see characters as they log in.
  const loadPlayers = async () => {
    try {
      const { data } = await api.get("/link/online-players");
      setPlayers(data.players || []);
    } catch {
      // silent — endpoint may temporarily 503 if all servers offline
    } finally {
      setPlayersLoading(false);
    }
  };
  useEffect(() => {
    loadPlayers();
    const t = setInterval(loadPlayers, 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const requestClickLink = async (p) => {
    if (p.is_linked) return toast.info("That character is already linked.");
    setConfirmCode("");
    setBusy(true);
    try {
      await api.post("/link/click-request", {
        server_name: p.server_name,
        player_name: p.player_name,
        steam_id: p.steam_id || null,
        player_userid: p.player_userid || null,
      });
      setPending({
        server_name: p.server_name,
        player_name: p.player_name,
        steam_id: p.steam_id,
        player_userid: p.player_userid || null,
        sent_at: Date.now(),
      });
      toast.success(`Code broadcast to ${p.player_name} in-game.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmClickLink = async () => {
    if (!pending) return;
    if (!confirmCode || confirmCode.length < 4) {
      return toast.error("Enter the 4-digit code from chat.");
    }
    setConfirming(true);
    try {
      const { data } = await api.post("/link/click-confirm", {
        server_name: pending.server_name,
        code: confirmCode.trim(),
      });
      setLinked(data.linked_servers || []);
      setPending(null);
      setConfirmCode("");
      toast.success(`Linked as ${data.player_name}.`);
      refreshProfile?.();
      loadPlayers();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setConfirming(false);
    }
  };

  const cancelPending = () => {
    setPending(null);
    setConfirmCode("");
  };

  const unlink = async (server_name) => {
    if (!window.confirm(`Unlink from ${server_name}?`)) return;
    try {
      await api.delete("/link/unlink", { data: { server_name } });
      setLinked((l) => l.filter((s) => s.server_name !== server_name));
      toast.success("Unlinked");
      refreshProfile();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (loading) return <Loader />;

  const pendingTtl = pending ? Math.max(0, 180000 - (now - pending.sent_at)) : 0;
  const pm = Math.floor(pendingTtl / 60000);
  const ps = Math.floor((pendingTtl % 60000) / 1000);
  const linkedKey = (sname, pname) =>
    `${sname || ""}::${(pname || "").toLowerCase()}`;
  const linkedSet = new Set(linked.map((l) => linkedKey(l.server_name, l.player_name)));

  return (
    <div>
      <PageHeader title="Bind Your Soul" subtitle="Link your in-game character to your Chillet's account." />

      {/* -------- Click-to-Link (controller / PS5 friendly) -------- */}
      <div className="glass-card p-7 fade-up mb-6" data-testid="click-to-link-card">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="label flex items-center gap-2">
              <Gamepad2 size={14} /> Click-to-Link
            </h3>
            <span className="inline-block mt-1.5 text-[10px] tracking-[0.18em] uppercase text-[var(--teal)] bg-[rgba(0,229,255,0.06)] border border-[var(--teal)]/30 px-2 py-0.5 rounded-full whitespace-nowrap">
              No typing in-game
            </span>
            <p className="text-[11px] text-[var(--muted)] mt-1">
              Pick your character from the live online list. We'll send a 4-digit code to your in-game chat — read it, enter it here, done.
            </p>
          </div>
          <button
            onClick={loadPlayers}
            className="btn btn-sm btn-secondary"
            data-testid="refresh-online-players"
            title="Refresh"
          >
            <RotateCw size={12} /> Refresh
          </button>
        </div>

        {pending ? (
          <div className="border border-[var(--teal-soft)] rounded-md p-4 fade-up" data-testid="click-to-link-pending">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-1">
              Awaiting confirmation
            </div>
            <div className="text-sm mb-3">
              Code broadcast to <span className="glow-teal">{pending.player_name}</span> on{" "}
              <span className="text-[var(--bone)]">{pending.server_name}</span>.
              Read the 4-digit code from chat and enter it below.
              <span className="text-[var(--muted)] ml-1">
                ({pm}:{String(ps).padStart(2, "0")} remaining)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                className="input flex-1 font-mono text-xl tracking-[0.3em] text-center"
                placeholder="• • • •"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                data-testid="click-to-link-code-input"
                autoFocus
              />
              <button
                onClick={confirmClickLink}
                disabled={confirming || confirmCode.length < 4}
                className="btn"
                data-testid="click-to-link-confirm"
              >
                <Check size={12} /> {confirming ? "Linking…" : "Confirm"}
              </button>
              <button
                onClick={cancelPending}
                className="btn btn-sm btn-secondary"
                data-testid="click-to-link-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : playersLoading ? (
          <div className="py-6 text-center text-[var(--muted)] text-sm">Loading online players…</div>
        ) : players.length === 0 ? (
          <div className="py-6 text-center text-[var(--muted)] text-sm" data-testid="no-online-players">
            <ChilletSkull size={28} className="wisp text-[var(--teal)] mx-auto mb-2" />
            No players online right now. Try again once someone (you) is in-game — the list refreshes every 6 seconds.
          </div>
        ) : (
          <ul
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[360px] overflow-y-auto pr-1"
            data-testid="online-players-list"
          >
            {players.map((p) => {
              const alreadyLinked = p.is_linked || linkedSet.has(linkedKey(p.server_name, p.player_name));
              return (
                <li key={`${p.server_name}-${p.player_name}-${p.steam_id || ""}`}>
                  <button
                    onClick={() => requestClickLink(p)}
                    disabled={busy || alreadyLinked}
                    data-testid={`online-player-${(p.steam_id || p.player_name).toLowerCase()}`}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${
                      alreadyLinked
                        ? "border-white/5 opacity-50 cursor-not-allowed"
                        : "border-white/10 hover:border-[var(--teal)] hover:bg-[rgba(0,229,255,0.04)]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{p.player_name}</div>
                        <div className="text-[10px] text-[var(--muted)] truncate">
                          {p.server_name.replace(/^Chillet's /, "")}
                          {p.level ? ` · Lv ${p.level}` : ""}
                        </div>
                      </div>
                      <span
                        className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                          alreadyLinked
                            ? "border-white/10 text-[var(--muted)]"
                            : "border-[var(--teal)]/40 text-[var(--teal)]"
                        }`}
                      >
                        {alreadyLinked ? "Linked" : "Pick"}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* -------- Linked servers -------- */}
      <div className="glass-card p-7 fade-up" style={{ animationDelay: "0.1s" }}>
        <h3 className="label mb-4">Linked Servers</h3>
        {linked.length ? (
          <ul className="divide-y divide-white/5">
            {linked.map((s) => (
              <li key={s.server_name} className="flex items-center justify-between py-3" data-testid="linked-row">
                <div>
                  <div className="text-[var(--bone)]">{s.server_name}</div>
                  <div className="text-xs text-[var(--muted)]">
                    Player: {s.player_name} {s.steam_id ? `· ${s.steam_id}` : ""}
                  </div>
                </div>
                <button onClick={() => unlink(s.server_name)} className="btn btn-sm btn-danger" data-testid={`unlink-${s.server_name}-button`}>
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-8 text-[var(--muted)]">
            <ChilletSkull size={36} className="wisp text-[var(--teal)] mx-auto mb-3" />
            <p>No servers linked yet. Hop in-game, then pick your character above.</p>
          </div>
        )}
      </div>
    </div>
  );
}
